import { CellClassRules, ColDef, ICellRendererParams } from 'ag-grid-community';

import { formatDate } from '~/shared/utils/formatNumber';
import { OptLogVolumeActions } from '~/shared/types/api';
import { Account } from '~/shared/utils/types';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';
import Typography from '~/shared/components/Typography';
import { getActionActorKey } from '~/shared/utils/actionActor';
import {
  getOptLogActionAfterDisplayRows,
  getOptLogActionAfterSortSizeGb,
  getOptLogActionBeforeDisplayRows,
  getOptLogActionBeforeSortSizeGb,
} from '~/shared/utils/optLogActionSnapshot';
import { isOptLogMultiMetricAction } from '~/shared/utils/optLogActionUtils';

import VolumeIdCell from './VolumeIdCell';
import TypeCell from './TypeCell';
import ActionActorCell from './ActionActorCell';
import ActionSnapshotCell from './ActionSnapshotCell';
import ReportsActionSnapshotCell from './ReportsActionSnapshotCell';

type OptLogVolumeActionsWithSkeleton = OptLogVolumeActions & { _isSkeleton?: boolean };

const optLogAlignWithTypeLabelClassRules: CellClassRules<OptLogVolumeActionsWithSkeleton> = {
  'opt-log-align-with-type-label': (params) =>
    !!params.data && !params.data._isSkeleton && isOptLogMultiMetricAction(params.data),
};

const optLogClipCellClass = 'opt-log-clip-cell';

export type OptLogSnapshotCellVariant = 'default' | 'reportsFigma';

interface CreateOptLogSnapshotColumnDefOptions {
  headerName: string;
  field: 'originalDiskSizeGb' | 'newDiskSizeGb';
  sortField: { field: string; dir: 'desc' | 'asc' };
  useReportsFigma: boolean;
  columnWidth: number;
  columnMaxWidth: number;
  cellClass: string;
  getDisplayRows: (action: OptLogVolumeActions) => ReturnType<
    typeof getOptLogActionBeforeDisplayRows
  >;
}

function createOptLogSnapshotColumnDef({
  headerName,
  field,
  sortField,
  useReportsFigma,
  columnWidth,
  columnMaxWidth,
  cellClass,
  getDisplayRows,
}: CreateOptLogSnapshotColumnDefOptions): ColDef<OptLogVolumeActionsWithSkeleton> {
  return {
    headerName,
    width: columnWidth,
    minWidth: columnWidth,
    maxWidth: columnMaxWidth,
    suppressSizeToFit: true,
    resizable: true,
    headerClass: 'size-header-nowrap',
    cellClass,
    field,
    valueGetter: ({ data }) => {
      if (!data || data._isSkeleton) {
        return undefined;
      }
      return field === 'originalDiskSizeGb'
        ? getOptLogActionBeforeSortSizeGb(data)
        : getOptLogActionAfterSortSizeGb(data);
    },
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={80} />;
      }
      if (!data) {
        return null;
      }
      const rows = getDisplayRows(data);
      if (useReportsFigma) {
        return <ReportsActionSnapshotCell rows={rows} />;
      }
      return <ActionSnapshotCell rows={rows} />;
    },
    sort: sortField?.field === field ? sortField?.dir : null,
  };
}

export function getOptLogBeforeAfterColumnDefs({
  sortField,
  snapshotCell = 'default',
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
  snapshotCell?: OptLogSnapshotCellVariant;
}): ColDef<OptLogVolumeActionsWithSkeleton>[] {
  const useReportsFigma = snapshotCell === 'reportsFigma';
  const columnWidth = useReportsFigma ? 160 : 140;
  const columnMaxWidth = useReportsFigma ? 180 : 160;
  const cellClass = useReportsFigma
    ? 'opt-log-snapshot-cell opt-log-snapshot-cell--figma'
    : 'opt-log-snapshot-cell';

  const shared = {
    sortField,
    useReportsFigma,
    columnWidth,
    columnMaxWidth,
    cellClass,
  };

  return [
    createOptLogSnapshotColumnDef({
      ...shared,
      headerName: 'Before',
      field: 'originalDiskSizeGb',
      getDisplayRows: getOptLogActionBeforeDisplayRows,
    }),
    createOptLogSnapshotColumnDef({
      ...shared,
      headerName: 'After',
      field: 'newDiskSizeGb',
      getDisplayRows: getOptLogActionAfterDisplayRows,
    }),
  ];
}

export function getActionByColumnDef(): ColDef<OptLogVolumeActionsWithSkeleton> {
  return {
    headerName: 'By',
    colId: 'by',
    sortable: false,
    initialWidth: 220,
    minWidth: 120,
    flex: 1,
    cellClass: optLogClipCellClass,
    cellClassRules: optLogAlignWithTypeLabelClassRules,
    cellRenderer: ({ data, node }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      if (!data?.type) {
        return null;
      }

      const rowKey = `${getActionActorKey(data)}-${node.rowIndex ?? 'row'}`;
      return <ActionActorCell action={data} rowKey={rowKey} />;
    },
  };
}

export const getOperationColumnDefs = ({
  sortField,
  isOrgView,
  accounts,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
  isOrgView?: boolean;
  accounts?: Account[];
}): ColDef<OptLogVolumeActionsWithSkeleton>[] => {
  const columns: ColDef<OptLogVolumeActions>[] = [
    {
      headerName: 'Volume ID',
      initialWidth: 240,
      minWidth: 220,
      flex: 1,
      field: 'volumeId',
      cellClass: optLogClipCellClass,
      cellClassRules: optLogAlignWithTypeLabelClassRules,
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={200} />;
        }
        return data?.volumeId ? (
          <VolumeIdCell id={data.volumeId} accountId={isOrgView ? data.accountId : undefined} />
        ) : null;
      },
      sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
    },
    {
      headerName: 'Instance ID',
      initialWidth: 180,
      minWidth: 170,
      flex: 1,
      field: 'instanceId',
      cellClass: optLogClipCellClass,
      cellClassRules: optLogAlignWithTypeLabelClassRules,
      sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={150} />;
        }
        if (!data?.instanceId) {
          return null;
        }
        return (
          <Typography variant="p3" fontWeight={500} className="min-w-0 max-w-full truncate">
            {data.instanceId}
          </Typography>
        );
      },
    },
    {
      headerName: 'Type',
      initialWidth: 280,
      minWidth: 270,
      flex: 1,
      field: 'type',
      cellRenderer: (params: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        const { data, node } = params;
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={200} />;
        }
        if (!data?.type) {
          return null;
        }
        const uniqueId = `${data.volumeId || 'vol'}-${data.actionFinishTime || node.rowIndex}`;
        return (
          <TypeCell
            key={uniqueId}
            type={data.type}
            volumeId={uniqueId}
            progress={data.progress}
            state={data.state}
          />
        );
      },
    },
    ...getOptLogBeforeAfterColumnDefs({ sortField, snapshotCell: 'reportsFigma' }),
    {
      headerName: 'Start Time',
      initialWidth: 160,
      minWidth: 144,
      flex: 1,
      field: 'actionStartTime',
      cellClassRules: optLogAlignWithTypeLabelClassRules,
      cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={120} />;
        }
        return value ? formatDate(value, true) : null;
      },
      sort: sortField?.field === 'actionStartTime' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
    },
    {
      headerName: 'End Time',
      initialWidth: 160,
      minWidth: 144,
      flex: 1,
      field: 'actionFinishTime',
      cellClassRules: optLogAlignWithTypeLabelClassRules,
      cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={120} />;
        }
        return data?.state && ['Success', 'Failure', 'Canceled'].includes(data.state)
          ? formatDate(value, true)
          : null;
      },
      sort: sortField?.field === 'actionFinishTime' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
    },
  ];
  if (isOrgView && accounts) {
    columns.push({
      headerName: 'Account',
      minWidth: 120,
      flex: 1,
      field: 'accountId',
      sortable: false,
      cellClass: optLogClipCellClass,
      cellClassRules: optLogAlignWithTypeLabelClassRules,
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={80} />;
        }

        const value = data?.accountId;
        const account = accounts?.find((acc) => acc.accountId === value);
        const displayName = account?.accountName || value || '-';

        return (
          <div className="flex items-center justify-start w-full">
            <Typography
              variant="p3"
              fontWeight={500}
              className="text-[14px] text-[#071031] text-left"
              truncate
            >
              {displayName}
            </Typography>
          </div>
        );
      },
    });
  }

  columns.push(getActionByColumnDef());

  return columns;
};
